import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { GoogleButton } from "@/components/AuthCallback";
import { api, errMsg } from "@/lib/api";
import { Brand } from "@/components/AppHeader";
import { CardSide } from "@/components/card/CardSide";
import { PREVIEW_TPL } from "@/lib/previewTpl";
import PlanPicker from "@/components/PlanPicker";

export default function Login() {
  const { user, login, register } = useAuth();
  const nav = useNavigate();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "", store_name: "", plan_id: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(() => sessionStorage.getItem("pvc_auth_msg") || "");
  useEffect(() => {
    const t = setTimeout(() => sessionStorage.removeItem("pvc_auth_msg"), 3000);
    return () => clearTimeout(t);
  }, []);
  const [info, setInfo] = useState(() => sessionStorage.getItem("pvc_auth_info") || "");
  useEffect(() => sessionStorage.removeItem("pvc_auth_info"), []);
  const [signup, setSignup] = useState("approval");
  useEffect(() => { api.get("/public/signup-mode").then((r) => setSignup(r.data.signup_mode)).catch(() => {}); }, []);

  if (user) return <Navigate to={user.role === "superadmin" ? "/admin" : "/"} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    setInfo("");
    sessionStorage.removeItem("pvc_auth_msg");
    try {
      if (mode === "login") {
        const u = await login(form.email, form.password);
        toast.success("Welcome back");
        nav(u.role === "superadmin" ? "/admin" : "/");
      } else {
        const res = await register(form.name, form.email, form.password, { store_name: form.store_name, plan_id: form.plan_id || null });
        if (res.pending) {
          setMode("login");
          setInfo(res.message);
          return;
        }
        toast.success("Account created");
        nav("/");
      }
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr] bg-slate-950">
      <div className="relative hidden lg:flex flex-col justify-between p-12 overflow-hidden border-r border-slate-800 studio-grid">
        <Brand />
        <div className="relative">
          <div className="absolute -inset-10 bg-amber-500/10 blur-3xl rounded-full" />
          <div className="relative flex flex-col gap-6 items-start">
            <div className="rotate-[-6deg] shadow-2xl shadow-black/60 rise" style={{ animationDelay: "80ms" }}>
              <CardSide tpl={PREVIEW_TPL.jewel} side="front" scale={5.2} rounded data={PREVIEW_TPL.jewelData} />
            </div>
            <div className="ml-40 -mt-24 rotate-[5deg] shadow-2xl shadow-black/60 rise" style={{ animationDelay: "220ms" }}>
              <CardSide tpl={PREVIEW_TPL.id} side="front" scale={4.6} rounded data={PREVIEW_TPL.idData} />
            </div>
          </div>
        </div>
        <div>
          <h1 className="font-heading text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.05] max-w-lg">
            Design. Merge data. <span className="text-amber-400">Print on PVC.</span>
          </h1>
          <p className="mt-4 text-sm text-slate-400 max-w-md leading-relaxed">
            Jewellery authenticity certificates on PVC — with a pixel-precise
            millimetre editor, CSV/Excel batch merge and print-ready PDF.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6">
        <form onSubmit={submit} className="w-full max-w-sm space-y-5 rise" data-testid="auth-form">
          <div className="lg:hidden mb-8"><Brand /></div>
          <div>
            <h2 className="font-heading text-2xl font-bold tracking-tight" data-testid="auth-title">
              {mode === "login" ? "Sign in to your studio" : "Create your studio"}
            </h2>
            <p className="text-sm text-slate-400 mt-1">Your templates and card data are saved to your account.</p>
          </div>
          <GoogleButton />
          <div className="flex items-center gap-3 text-[11px] uppercase tracking-wider text-slate-500"><span className="h-px flex-1 bg-slate-800" />or with email<span className="h-px flex-1 bg-slate-800" /></div>
          {mode === "register" && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Store name"><input className="cf-input" value={form.store_name} onChange={set("store_name")} data-testid="register-store-input" placeholder="New Amina Jewellers" /></Field>
                <Field label="Your name"><input className="cf-input" value={form.name} onChange={set("name")} data-testid="register-name-input" placeholder="Owner name" /></Field>
              </div>
              <PlanPicker value={form.plan_id} onChange={(plan_id) => setForm({ ...form, plan_id })} />
            </>
          )}
          <Field label="Email"><input type="email" required className="cf-input" value={form.email} onChange={set("email")} data-testid="auth-email-input" placeholder="you@company.com" /></Field>
          <Field label="Password"><input type="password" required minLength={6} className="cf-input" value={form.password} onChange={set("password")} data-testid="auth-password-input" placeholder="••••••" /></Field>
          {error && <p className="text-sm text-red-400" data-testid="auth-error">{error}</p>}
          {info && <p className="text-sm text-emerald-300 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-3 py-2" data-testid="auth-info">{info}</p>}
          <button disabled={busy} className="cf-btn-primary w-full h-11" data-testid="auth-submit-button">
            {busy && <Loader2 size={16} className="animate-spin" />} {mode === "login" ? "Sign in" : "Create account"}
          </button>
          {signup === "closed" ? (
            <p className="text-xs text-slate-500 text-center" data-testid="signup-closed-note">New store accounts are created by the administrator.</p>
          ) : (
          <p className="text-sm text-slate-400 text-center">
            {mode === "login" ? "New here?" : "Already have an account?"}{" "}
            <button type="button" className="text-amber-400 hover:text-amber-300 font-semibold" data-testid="auth-toggle-mode"
              onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); setInfo(""); }}>
              {mode === "login" ? "Create an account" : "Sign in"}
            </button>
          </p>
          )}
          <Link to="/join" className="block text-center text-sm rounded-lg border border-amber-500/30 bg-amber-500/5 py-2.5 text-amber-300 hover:bg-amber-500/10" data-testid="login-join-link">Jeweller? Start a free trial or choose a plan →</Link>
          {mode === "register" && signup === "approval" && <p className="text-[11px] text-slate-500 text-center">New accounts are activated after administrator approval.</p>}
        </form>
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <label className="block space-y-1.5">
    <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">{label}</span>
    {children}
  </label>
);
